import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useAudioWorkletRecorder — captures mono 16-bit PCM @ 16 kHz from the mic
 * via AudioWorklet, eliminating the codec-warmup delay that makes
 * MediaRecorder swallow the first word of an utterance.
 *
 * Lifecycle:
 *   1. On mount: build AudioContext (suspended) + load worklet module.
 *      No mic permission requested yet.
 *   2. start():
 *      a. If first call ever, request getUserMedia (this is the user-gesture
 *         requirement on iOS Safari).
 *      b. resume() the context.
 *      c. Connect MediaStreamSource → AudioWorkletNode → (no output sink
 *         to avoid hearing yourself).
 *   3. Each PCM frame from the worklet is pushed to a ref array; consumers
 *      read the latest peak amplitude via the `peak` state.
 *   4. stop(): disconnects, returns the accumulated frames + sampleRate.
 *
 * Silence-based auto-finish lives in the consumer (PTTButton) — it watches
 * `peak` and decides whether silence should send or cancel, since that
 * policy differs per UI surface.
 */

const TARGET_SAMPLE_RATE = 16_000;
const WORKLET_URL = '/audio-worklet/pcm-recorder.worklet.js';

export interface UseRecorderArgs {
    onPeak?: (amp: number) => void;
}

export type PermissionState = 'unknown' | 'pending' | 'granted' | 'denied';

export interface RecorderHandle {
    state: 'idle' | 'requesting' | 'recording' | 'error';
    /** Whether the AudioContext + worklet are loaded (browser-supported). */
    isSupported: boolean;
    error: string | null;
    /** Peak amplitude (0..1) of the most recent frame, for the visualizer. */
    peak: number;
    /** Mic permission state — separated from `state` so the UI can lock the
     *  capture button until the OS dialog is resolved. */
    permission: PermissionState;
    /**
     * Request mic permission ahead of any recording. Call this when the user
     * enters voice mode so the browser's permission dialog appears OUTSIDE
     * the press-to-record flow (otherwise iOS Safari/Chrome eat the pointer
     * gesture while the dialog is up and the recording state goes haywire).
     * Idempotent — returns immediately if mic is already granted.
     */
    prewarm: () => Promise<void>;
    /** Begin (or resume) recording. Idempotent. */
    start: () => Promise<void>;
    /**
     * Stop recording and return the buffered frames. After stop(), the
     * mic stream stays alive for fast re-start; call dispose() to release it.
     */
    stop: () => { frames: Float32Array[]; sampleRate: number };
    /** Cancel without producing frames (used for swipe-to-cancel). */
    cancel: () => void;
    /** Release mic + close AudioContext. Call on unmount. */
    dispose: () => Promise<void>;
}

export function useAudioWorkletRecorder(args: UseRecorderArgs = {}): RecorderHandle {
    const { onPeak } = args;

    const audioCtxRef = useRef<AudioContext | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const workletRef = useRef<AudioWorkletNode | null>(null);
    const framesRef = useRef<Float32Array[]>([]);

    const [state, setState] = useState<RecorderHandle['state']>('idle');
    const [error, setError] = useState<string | null>(null);
    const [peak, setPeak] = useState<number>(0);
    const [isSupported, setIsSupported] = useState<boolean>(false);
    const [permission, setPermission] = useState<PermissionState>('unknown');

    // Build (suspended) context + load worklet module on mount.
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            try {
                const Ctx: typeof AudioContext =
                    window.AudioContext ||
                    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
                if (!Ctx) {
                    setIsSupported(false);
                    return;
                }
                const ctx = new Ctx({ sampleRate: TARGET_SAMPLE_RATE });
                if (cancelled) {
                    await ctx.close();
                    return;
                }
                if (typeof ctx.audioWorklet?.addModule !== 'function') {
                    setIsSupported(false);
                    await ctx.close();
                    return;
                }
                await ctx.audioWorklet.addModule(WORKLET_URL);
                if (cancelled) {
                    await ctx.close();
                    return;
                }
                audioCtxRef.current = ctx;
                setIsSupported(true);
            } catch (err) {
                if (!cancelled) {
                    setIsSupported(false);
                    setError((err as Error).message);
                }
            }
        };
        init();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ensureMic = useCallback(async () => {
        if (streamRef.current) return streamRef.current;
        setPermission('pending');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: TARGET_SAMPLE_RATE,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
            setPermission('granted');
            return stream;
        } catch (err) {
            const name = (err as Error).name;
            setPermission(name === 'NotAllowedError' ? 'denied' : 'unknown');
            throw err;
        }
    }, []);

    /**
     * Pull mic permission ahead of the first recording. The OS dialog will
     * appear here instead of in the middle of the tap-to-record gesture,
     * which on iOS Chrome/Safari otherwise eats the pointer state and
     * triggers a cascade of phantom short recordings.
     */
    const prewarm = useCallback(async () => {
        if (streamRef.current) return;
        try {
            await ensureMic();
        } catch {
            // Permission state is already updated by ensureMic; let the UI
            // read `permission` and decide how to surface the denial.
        }
    }, [ensureMic]);

    const start = useCallback(async () => {
        if (state === 'recording') return;
        if (!audioCtxRef.current) {
            setError('audio_context_unavailable');
            setState('error');
            return;
        }
        try {
            setState('requesting');
            const stream = await ensureMic();
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') await ctx.resume();

            const source = ctx.createMediaStreamSource(stream);
            const worklet = new AudioWorkletNode(ctx, 'pcm-recorder');
            framesRef.current = [];

            worklet.port.onmessage = (evt) => {
                const frame = evt.data as Float32Array;
                framesRef.current.push(frame);

                // Peak amplitude for visualizer.
                let p = 0;
                for (let i = 0; i < frame.length; i++) {
                    const a = Math.abs(frame[i]);
                    if (a > p) p = a;
                }
                setPeak(p);
                onPeak?.(p);
            };

            source.connect(worklet);
            // Intentionally NOT connecting `worklet` to ctx.destination —
            // we don't want the user to hear themselves.
            sourceRef.current = source;
            workletRef.current = worklet;
            setState('recording');
            setError(null);
        } catch (err) {
            const msg = (err as Error).name === 'NotAllowedError'
                ? 'permission_denied'
                : (err as Error).message;
            setError(msg);
            setState('error');
        }
    }, [state, ensureMic, onPeak]);

    const stop = useCallback((): { frames: Float32Array[]; sampleRate: number } => {
        const ctx = audioCtxRef.current;
        const sampleRate = ctx?.sampleRate ?? TARGET_SAMPLE_RATE;
        if (workletRef.current) {
            try {
                workletRef.current.port.postMessage({ type: 'stop' });
                workletRef.current.disconnect();
            } catch { /* noop */ }
            workletRef.current = null;
        }
        if (sourceRef.current) {
            try { sourceRef.current.disconnect(); } catch { /* noop */ }
            sourceRef.current = null;
        }
        const frames = framesRef.current;
        framesRef.current = [];
        setState('idle');
        setPeak(0);
        return { frames, sampleRate };
    }, []);

    const cancel = useCallback(() => {
        framesRef.current = [];
        stop();
    }, [stop]);

    const dispose = useCallback(async () => {
        cancel();
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        const ctx = audioCtxRef.current;
        audioCtxRef.current = null;
        if (ctx && ctx.state !== 'closed') {
            try { await ctx.close(); } catch { /* noop */ }
        }
    }, [cancel]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispose().catch(() => undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { state, isSupported, error, peak, permission, prewarm, start, stop, cancel, dispose };
}
